/**
 * Merged prompt/tool-delivery module for the unified ConnectorManager.
 *
 * Combines the responsibilities of:
 *   - `mount-prompt.ts`    — build a system-prompt section for filesystem-face connectors
 *   - `connector-tools.ts` — build an SDK MCP server + system-prompt section for tool-face connectors
 *
 * Two exports:
 *   1. `buildConnectorPromptSection(manager, options?)` — markdown string for the
 *      agent's system prompt. Iterates the unified manager's connectors and:
 *        - if `connector.filesystem` is present → emits a Mounts table row (ported from mount-prompt.ts)
 *        - if `connector.tools` is present      → emits a Connectors list entry (ported from connector-tools.ts)
 *        - dual-face connectors emit BOTH
 *
 *   2. `buildSdkConnectorTools(manager, options?)` — in-process MCP server config for
 *      the Claude Agent SDK. Only tool-face connectors are wired to tools; filesystem-only
 *      connectors are silently excluded.
 *
 * @docLink packages/connectors/concepts#connector-prompt
 */

// Static namespace import so bun --compile bundles zod into the binary.
// Dynamic import("zod") via a string variable evades bun's static analysis
// and fails at runtime inside /$bunfs/. The unwrap (zNS.z ?? zNS.default
// ?? zNS) handles both bun's native ESM and vitest's CJS-interop resolver,
// which return the zod namespace at different keys.
import * as zNS from "zod";
import type { ConnectorManager } from "./connector-manager.js";
import type { FailedConnectorInfo } from "./connector-types.js";

const zStatic: any = (zNS as any).z ?? (zNS as any).default ?? zNS;

/**
 * Read the manager's failed-connector list defensively.
 *
 * Prompt building must never crash on a manager that predates the method or on
 * a hand-rolled test double, so an older/mocked manager degrades to "nothing
 * failed" instead of throwing.
 */
function readFailedConnectors(manager: ConnectorManager): FailedConnectorInfo[] {
  if (typeof manager.listFailedConnectors !== "function") return [];
  try {
    return manager.listFailedConnectors();
  } catch {
    return [];
  }
}

// ── Options ───────────────────────────────────────────────────────────────────

/**
 * Options for `buildSdkConnectorTools`.
 * @docLink packages/connectors/api-reference#connector-tool-options
 */
export interface ConnectorToolOptions {
  /** Pre-loaded `@anthropic-ai/claude-agent-sdk` instance (optional; auto-imported if absent). */
  sdk?: any;
  /** Pre-loaded `zod` namespace (optional; uses static import if absent). */
  z?: any;
  /** Whether the agent has shell access. Defaults to true. When true, `connector_exec` is omitted. */
  shellAccess?: boolean;
}

/**
 * Options for `buildConnectorPromptSection`.
 * @docLink packages/connectors/api-reference#connector-prompt-options
 */
export interface ConnectorPromptOptions {
  /** Whether the agent has shell access. Defaults to true. Affects CLI vs SDK tool examples. */
  shellAccess?: boolean;
}

// ── SDK tool builder ──────────────────────────────────────────────────────────

/**
 * Build an in-process MCP server exposing connector tools for the Claude Agent SDK.
 * Only connectors with a `tools` face are wired; filesystem-only connectors are excluded.
 * Returns the server config to pass as `mcpServers` in the SDK query options.
 * Returns `null` if `@anthropic-ai/claude-agent-sdk` is not available.
 *
 * @param manager - Connected unified `ConnectorManager`.
 * @param options - Optional SDK/Zod overrides and shell access flag.
 * @returns MCP server config object or `null`.
 * @docLink packages/connectors/api-reference#build-sdk-connector-tools
 */
export async function buildSdkConnectorTools(
  manager: ConnectorManager,
  options?: ConnectorToolOptions,
): Promise<object | null> {
  let sdk: any = options?.sdk;
  if (!sdk) {
    try {
      sdk = await import("@anthropic-ai/claude-agent-sdk");
    } catch {
      return null;
    }
  }

  return sdk.createSdkMcpServer({
    name: "skaile-connectors",
    tools: buildConnectorToolDefs(manager, options),
  });
}

/** SDK-free definitions shared by the managed registry adapter and Claude SDK. */
export function buildConnectorToolDefs(
  manager: ConnectorManager,
  options?: ConnectorToolOptions,
): ConnectorToolDef[] {
  const shellAccess = options?.shellAccess ?? true;
  const z: any = options?.z ?? zStatic;

  const tools: any[] = [];

  // ── Discovery ──

  tools.push({
    name: "connector_list",
    description:
      "List connectors as {connected, failed}. `connected` holds the usable connectors with their id, driver, access mode, and available operations; `failed` holds connectors that did not connect this session, with the failure reason — their mount paths are dead directories, not synced workspaces. Use this to discover connectors that may have been added after session start (e.g. shared-state stores like 'presence' or 'session' pushed by the host).",
    inputSchema: {},
    handler: async () => {
      try {
        const entries = {
          connected: manager.listConnectors(),
          failed: readFailedConnectors(manager),
        };
        return { content: [{ type: "text" as const, text: JSON.stringify(entries, null, 2) }] };
      } catch (err: any) {
        return {
          content: [{ type: "text" as const, text: `Error: ${err.message}` }],
          isError: true,
        };
      }
    },
  });

  tools.push({
    name: "connector_load_skill",
    description:
      "Load full instructions for a specific connector into context. Returns a markdown document describing the connector and its available CLI commands.",
    inputSchema: {
      connector_id: z.string().describe("Connector ID (use connector_list to enumerate)"),
    },
    handler: async ({ connector_id }: { connector_id: string }) => {
      try {
        const body = manager.loadSkill(connector_id, shellAccess);
        return { content: [{ type: "text" as const, text: body }] };
      } catch (err: any) {
        return {
          content: [{ type: "text" as const, text: `Error: ${err.message}` }],
          isError: true,
        };
      }
    },
  });

  // ── Base CRUD (tool-face connectors only) ──

  tools.push({
    name: "connector_read",
    description:
      'Read content from a connector by path. For KV stores, path is the key. For databases, path is "table/pk".',
    inputSchema: {
      connector_id: z.string().describe("Connector ID"),
      path: z.string().describe("Path within the connector"),
    },
    handler: async ({ connector_id, path }: { connector_id: string; path: string }) => {
      try {
        const content = await manager.read(connector_id, path);
        return {
          content: [{ type: "text" as const, text: content?.data?.toString() ?? "(empty)" }],
        };
      } catch (err: any) {
        return {
          content: [{ type: "text" as const, text: `Error: ${err.message}` }],
          isError: true,
        };
      }
    },
  });

  tools.push({
    name: "connector_write",
    description: "Write content to a connector at a path.",
    inputSchema: {
      connector_id: z.string().describe("Connector ID"),
      path: z.string().describe("Path within the connector"),
      content: z.string().describe("Content to write"),
    },
    handler: async ({
      connector_id,
      path,
      content,
    }: {
      connector_id: string;
      path: string;
      content: string;
    }) => {
      try {
        await manager.write(connector_id, path, { data: content });
        return { content: [{ type: "text" as const, text: "Written." }] };
      } catch (err: any) {
        return {
          content: [{ type: "text" as const, text: `Error: ${err.message}` }],
          isError: true,
        };
      }
    },
  });

  tools.push({
    name: "connector_list_entries",
    description: "List entries in a connector (files, keys, rows, etc).",
    inputSchema: {
      connector_id: z.string().describe("Connector ID"),
      path: z.string().optional().describe("Path prefix or directory"),
      recursive: z.boolean().optional().describe("Include nested entries"),
    },
    handler: async ({
      connector_id,
      path,
      recursive,
    }: {
      connector_id: string;
      path?: string;
      recursive?: boolean;
    }) => {
      try {
        const entries = await manager.list(connector_id, path, { recursive });
        return { content: [{ type: "text" as const, text: JSON.stringify(entries, null, 2) }] };
      } catch (err: any) {
        return {
          content: [{ type: "text" as const, text: `Error: ${err.message}` }],
          isError: true,
        };
      }
    },
  });

  tools.push({
    name: "connector_search",
    description: "Search within a connector for matching content.",
    inputSchema: {
      connector_id: z.string().describe("Connector ID"),
      query: z.string().describe("Search query"),
    },
    handler: async ({ connector_id, query }: { connector_id: string; query: string }) => {
      try {
        const results = await manager.search(connector_id, query);
        return { content: [{ type: "text" as const, text: JSON.stringify(results, null, 2) }] };
      } catch (err: any) {
        return {
          content: [{ type: "text" as const, text: `Error: ${err.message}` }],
          isError: true,
        };
      }
    },
  });

  tools.push({
    name: "connector_delete",
    description: "Delete an entry from a connector.",
    inputSchema: {
      connector_id: z.string().describe("Connector ID"),
      path: z.string().describe("Path to delete"),
    },
    handler: async ({ connector_id, path }: { connector_id: string; path: string }) => {
      try {
        const ok = await manager.delete(connector_id, path);
        return { content: [{ type: "text" as const, text: ok ? "Deleted." : "Not found." }] };
      } catch (err: any) {
        return {
          content: [{ type: "text" as const, text: `Error: ${err.message}` }],
          isError: true,
        };
      }
    },
  });

  // ── Fallback: connector_exec (only when no shell access) ──

  if (!shellAccess) {
    tools.push({
      name: "connector_exec",
      description:
        "Execute a custom connector operation by name. Use only when shell access is unavailable. Load the connector skill first to see available operations and their arguments.",
      inputSchema: {
        connector_id: z.string().describe("Connector ID"),
        operation: z.string().describe("Operation name (e.g. 'send_message', 'query')"),
        args: z
          .record(z.string(), z.unknown())
          .describe("Named arguments for the operation (without -- prefix)"),
      },
      handler: async ({
        connector_id,
        operation,
        args,
      }: {
        connector_id: string;
        operation: string;
        args: Record<string, unknown>;
      }) => {
        try {
          const result = await manager.executeOp(connector_id, operation, args);
          return { content: [{ type: "text" as const, text: result }] };
        } catch (err: any) {
          return {
            content: [{ type: "text" as const, text: `Error: ${err.message}` }],
            isError: true,
          };
        }
      },
    });
  }

  return tools;
}

// ── Flow agent-tool SDK server ──────────────────────────────────────────────────

/**
 * Agent-facing flow operations exposed as first-class SDK tools. Host-only ops
 * (`applyApproval`, `applyInput`, `cancel`, `setAutonomousMode`, `retryNode`,
 * `hydrate`, `start`) are intentionally NOT exposed to the agent — the agent
 * must not approve its own gates, restart, or cancel its own run. Keep in sync
 * with `FlowAdapter.describeOperations` (the read/agent-write ops only).
 */
const FLOW_AGENT_OPS: ReadonlySet<string> = new Set([
  "get_available",
  "get_state",
  "get_globals",
  "build_handoff",
  "validate_node",
  "start_node",
  "request_approval",
  "request_input",
  "complete_node",
  "skip_node",
  "fail_node",
]);

/**
 * Build an in-process MCP server exposing the flow connector's agent operations
 * as **one callable tool per operation** (`start_node`, `complete_node`,
 * `request_approval`, …), surfaced to the model as
 * `mcp__skaile-flow__<op>`.
 *
 * The generic `buildSdkConnectorTools` never emits per-operation tools — a
 * claude-sdk session with shell access gets no `connector_exec` tool either —
 * so a real agent could not discover or invoke the flow node-advancement tools
 * the orchestrator prompt tells it to call (workspaces#399). This dedicated
 * server closes that gap for the one connector whose operations ARE the agent's
 * turn surface. Each tool handler dispatches to `manager.executeOp(connectorId,
 * op, args)`.
 *
 * @param manager - Connected unified `ConnectorManager`.
 * @param connectorId - The active flow connector id (`flow:<runId>`).
 * @param options - Optional SDK/Zod overrides.
 * @returns MCP server config object, or `null` if the SDK is unavailable or the
 *   connector exposes no agent operations.
 * @docLink packages/connectors/api-reference#build-sdk-flow-tools
 * @since 3.6.0
 */
export async function buildSdkFlowTools(
  manager: ConnectorManager,
  connectorId: string,
  options?: ConnectorToolOptions,
): Promise<object | null> {
  let sdk: any = options?.sdk;
  if (!sdk) {
    try {
      sdk = await import("@anthropic-ai/claude-agent-sdk");
    } catch {
      return null;
    }
  }
  const tools = buildFlowToolDefs(manager, connectorId, options);
  return tools.length ? sdk.createSdkMcpServer({ name: "skaile-flow", tools }) : null;
}

/** Agent-only flow operations; host approvals and lifecycle operations stay private. */
export function buildFlowToolDefs(
  manager: ConnectorManager,
  connectorId: string,
  options?: ConnectorToolOptions,
): ConnectorToolDef[] {
  const z: any = options?.z ?? zStatic;

  let ops: ReturnType<ConnectorManager["getOperations"]>;
  try {
    ops = manager.getOperations(connectorId);
  } catch {
    return [];
  }
  const agentOps = ops.filter((op) => FLOW_AGENT_OPS.has(op.name));
  if (agentOps.length === 0) return [];

  const tools = agentOps.map((op) => ({
    name: op.name,
    description: op.description,
    inputSchema: buildOperationInputSchema(op.args, z),
    handler: async (input: Record<string, unknown>) => {
      try {
        const result = await manager.executeOp(connectorId, op.name, input ?? {});
        return { content: [{ type: "text" as const, text: result || "(ok)" }] };
      } catch (err: any) {
        return {
          content: [{ type: "text" as const, text: `Error: ${err.message}` }],
          isError: true,
        };
      }
    },
  }));

  return tools;
}

/** Build a Zod input-schema record from an operation's argument descriptors. */
function buildOperationInputSchema(
  args: Array<{ name: string; description: string; type: string; required: boolean }>,
  z: any,
): Record<string, unknown> {
  const shape: Record<string, unknown> = {};
  for (const arg of args) {
    let schema: any;
    switch (arg.type) {
      case "boolean":
        schema = z.boolean();
        break;
      case "number":
        schema = z.number();
        break;
      case "json":
        schema = z.json();
        break;
      default:
        schema = z.string();
    }
    if (arg.description) schema = schema.describe(arg.description);
    if (!arg.required) schema = schema.optional();
    shape[arg.name] = schema;
  }
  return shape;
}

// ── System prompt section builder ─────────────────────────────────────────────

/**
 * Build a markdown section describing all connectors for the agent's system prompt.
 *
 * Iterates the unified `ConnectorManager` and, per connector:
 *   - **Filesystem face** (`connector.filesystem != null`) → emits a row in the
 *     `## Mounts` table (id, driver, mount path, access level), plus Tier-1/Tier-2
 *     git auth guidance for `git` connectors (ported from `mount-prompt.ts`).
 *   - **Tool face** (`connector.tools != null`) → emits an entry in the
 *     `## Connectors` section (ported from `connector-tools.ts`).
 *   - Dual-face connectors appear in BOTH sections.
 *
 * @param manager - Connected unified `ConnectorManager`.
 * @param options - Optional shell access flag.
 * @returns Markdown string to append to the system prompt, or empty string when
 *   no connectors are connected.
 * @docLink packages/connectors/api-reference#build-connector-prompt-section
 */
export function buildConnectorPromptSection(
  manager: ConnectorManager,
  options?: ConnectorPromptOptions,
): string {
  const shellAccess = options?.shellAccess ?? true;
  const allConnectors = manager.listConnectors();
  const failed = readFailedConnectors(manager);
  // A session whose only connector failed must still hear about it — returning
  // "" here is what let the failure disappear from the prompt entirely.
  if (allConnectors.length === 0 && failed.length === 0) return "";

  const sections: string[] = [];

  // ── Resolve each connector's faces in a single pass (I-2) ───────────────────
  // Each connector is looked up once; the result is shared by both the filesystem
  // section and the tool section below. A connector with neither face is silently
  // omitted — in the unified Connector model every connector is expected to have
  // at least one face (filesystem or tools). A face-less connector is a
  // misconfiguration, not a normal state, so excluding it from the prompt is
  // correct-by-design (unified-model invariant: every connector has ≥1 face).
  const faces = allConnectors.map((c) => {
    try {
      const e = manager.get(c.id);
      return { c, fs: e.connector.filesystem != null, tools: e.connector.tools != null };
    } catch {
      return { c, fs: false, tools: false };
    }
  });

  const fsConnectors = faces.filter((f) => f.fs).map((f) => f.c);
  const toolConnectors = faces.filter((f) => f.tools).map((f) => f.c);

  // ── Filesystem-face section (ported from mount-prompt.ts) ────────────────────

  if (fsConnectors.length > 0) {
    sections.push(buildFilesystemSection(manager, fsConnectors));
  }

  // ── Tool-face section (ported from connector-tools.ts) ───────────────────────

  if (toolConnectors.length > 0) {
    sections.push(buildToolSection(manager, toolConnectors, shellAccess));
  }

  // First, always: the rest of this prompt — and the host platform's own
  // preamble, which we cannot edit from here — describes these paths as live
  // managed workspaces. The correction has to be read before the claim it
  // corrects.
  if (failed.length > 0) {
    sections.unshift(buildFailedConnectorSection(failed));
  }

  return sections.join("\n\n");
}

// ── System prompt section helpers ─────────────────────────────────────────────

/** Connector descriptors as returned by `ConnectorManager.listConnectors()`. */
type ConnectorEntry = ReturnType<ConnectorManager["listConnectors"]>[number];

/**
 * Build the `## Mount failures` section for connectors that never connected.
 *
 * This section is the agent's only signal that a mount is dead. Everything else
 * it reads — this prompt's `## Mounts` table, the host platform's preamble —
 * is built from the *connected* set and therefore says nothing at all about
 * these paths, while the lifecycle automation that would normally save the
 * agent's work (auto-commit, push-after-commit, push-on-hibernate,
 * commit-on-hibernate, push-on-close) is wired to a connector that does not
 * exist. So the section has to state the override explicitly rather than
 * merely coexist with the claim it contradicts.
 */
function buildFailedConnectorSection(failed: FailedConnectorInfo[]): string {
  const lines: string[] = [
    "## Mount failures — read this first",
    "",
    "The following connectors **failed to connect** in this session. Their directories " +
      "are ordinary local folders: **not** connected, **not** synced, and **not** " +
      "backend-managed git worktrees. Any other statement in this prompt — including the " +
      "host platform's own description of the workspace — that describes these paths as a " +
      "managed, synced, or version-controlled workspace **is superseded by this section**.",
    "",
    "| Mount | Driver | Path | Access | Required | Failure |",
    "|---|---|---|---|---|---|",
  ];

  for (const f of failed) {
    const path = f.mountPath ?? f.source ?? "(no mount)";
    // `required` is the difference between a degraded session and one that came
    // up broken: a required read-write mount failing means the session started
    // with no way to save anything at all.
    const required = f.required ? "yes" : "no";
    lines.push(
      `| ${f.id} | ${f.driver} | \`${path}\` | ${f.access} | ${required} | ${oneLine(f.error)} |`,
    );
  }

  lines.push("");
  lines.push(
    "**Work written under these paths is not saved.** The mount's lifecycle automation — " +
      "auto-commit, push-after-commit, push-on-hibernate, commit-on-hibernate, " +
      "push-on-close — is dead for a failed connector: nothing is committed, nothing is " +
      "pushed, and the contents do not survive the session. Do not tell the user their " +
      "work is saved, synced, or pushed. If they ask you to write substantial work into " +
      "one of these paths, say first that it will be lost. `Required: yes` marks a " +
      "read-write mount the session was supposed to be built on — that session did not " +
      "start degraded, it started with nowhere to save anything.",
  );

  lines.push("");
  lines.push(
    "**There is nothing to fix inside this workspace.** The credential for a mount is " +
      "minted by the host platform, not by anything in here and not by any project " +
      "setting — there is no credential field to fill in. Report the failure text above " +
      "verbatim to the user and ask them to reconnect the connector's provider link on " +
      "the platform, then restart the session. Do not retry the connection yourself; a " +
      "connector that failed at startup stays failed until the session restarts.",
  );

  if (failed.some((f) => f.driver === "git")) {
    lines.push("");
    lines.push(
      "**Git specifically:** `git fetch` and `git ls-remote` can still succeed against a " +
        "public remote with no credential at all, so a working read is **not** evidence " +
        "that auth works. Only writes need the credential. `git push` will fail with " +
        "`skaile: mount not connected: …`. Do not retry it in a loop and do not attempt " +
        "to configure git credentials by hand.",
    );
  }

  return lines.join("\n");
}

/** Collapse newlines so an error message cannot break the markdown table row. */
function oneLine(text: string): string {
  return text
    .replace(/\s*[\r\n]+\s*/g, " ")
    .replace(/\|/g, "\\|")
    .trim();
}

/**
 * Build the `## Mounts` filesystem-face section: the mount table, the
 * unavailable-mounts note, and the Tier-1/Tier-2 git auth notes.
 */
function buildFilesystemSection(manager: ConnectorManager, fsConnectors: ConnectorEntry[]): string {
  const lines: string[] = [
    "## Mounts",
    "",
    "The following directories are mounted resources. Read and write files normally.",
    "",
    "| Mount | Driver | Path | Access |",
    "|---|---|---|---|",
  ];

  // A mount whose status is not "connected" is a deferred remote mount that is
  // still syncing (or failed) — its target is held read-only so the agent
  // cannot write a phantom workspace into it. Surface that instead of a normal
  // writable row; the platform flips it on the connected status event.
  let anyUnavailable = false;
  for (const c of fsConnectors) {
    const mountPath = c.mountPath ?? "(unmounted)";
    if (c.status === "connected") {
      lines.push(`| ${c.id} | ${c.driver} | \`${mountPath}/\` | ${c.access} |`);
    } else {
      anyUnavailable = true;
      const state = c.status === "error" ? "failed" : "syncing";
      lines.push(
        `| ${c.id} | ${c.driver} | \`${mountPath}/\` | unavailable (${state}) — read-only, do not write |`,
      );
    }
  }

  if (anyUnavailable) {
    lines.push("");
    lines.push(
      "> **Unavailable mounts are read-only at the filesystem layer.** A mount marked " +
        "`unavailable (syncing / failed)` is not live yet — its directory is mounted read-only, " +
        "so writes fail. Do not create files there: the contents are not the real workspace, and " +
        "anything you write would be a phantom that diverges from the remote once it mounts. Wait " +
        "for it to become writable (watch the connector status).",
    );
  }

  appendGitAuthNotes(lines, manager, fsConnectors);

  return lines.join("\n");
}

/**
 * Append the Tier-2 / Tier-1 git auth notes (spec: 2026-05-05-git-credential-tiers).
 * Intersect listGitConnectors() with the filesystem-face set so the note
 * and the mount table stay coherent: a git connector with only a tools face
 * (no filesystem face) is absent from the table and must not drive a note
 * inside ## Mounts. listGitConnectors() does NOT filter by face — it returns
 * all connected git-driver connectors regardless of which faces they expose.
 */
function appendGitAuthNotes(
  lines: string[],
  manager: ConnectorManager,
  fsConnectors: ConnectorEntry[],
): void {
  const fsConnectorIds = new Set(fsConnectors.map((c) => c.id));
  const gitConnectors = manager.listGitConnectors().filter((g) => fsConnectorIds.has(g.id));
  if (gitConnectors.length === 0) return;

  const tier2 = gitConnectors.filter((m) => m.exposeAccessToken === true);
  const tier1 = gitConnectors.filter((m) => m.exposeAccessToken !== true);

  if (tier2.length > 0) {
    const hosts = [...new Set(tier2.map((m) => safeHost(m.source)))].filter(Boolean);
    lines.push("");
    lines.push(
      `> **Git auth (CLI):** the git CLI is authenticated for the duration of this session ` +
        `for ${hosts.map((h) => `\`${h}\``).join(", ")}. Use \`git push\` / \`git pull\` directly.`,
    );
  }
  if (tier1.length > 0) {
    lines.push("");
    lines.push(
      "> **Git auth (mount-managed):** `git push` / `git pull` against mount remotes go " +
        "through the workspace mount, not the agent. If a network git command fails with " +
        '"could not read Username", do not retry — the credential is intentionally not ' +
        "exposed to the CLI. Use the workspace's autoPush / autoPull or ask the user to " +
        'enable "Allow agent to use git CLI directly" on the mount.',
    );
  }
}

/** Build the `## Connectors` tool-face section. */
function buildToolSection(
  manager: ConnectorManager,
  toolConnectors: ConnectorEntry[],
  shellAccess: boolean,
): string {
  const lines: string[] = ["## Connectors", ""];

  lines.push("Currently registered connectors (id — driver, access):");
  for (const r of toolConnectors) {
    lines.push(`- \`${r.id}\` — ${r.driver}, ${r.access}`);
  }
  lines.push("");
  lines.push(
    "Additional connectors may be registered mid-session by the host — notably shared-state stores (driver: `xstate-store`, e.g. `session`, `presence`, per-component stores). Re-enumerate with `connector_list` whenever you need the live set.",
  );
  lines.push("");

  // Eager preset instructions — one block per connector that has a body.
  for (const r of toolConnectors) {
    let instructions: string | undefined;
    try {
      instructions = manager.get(r.id).declaration.instructions;
    } catch {
      // connector no longer active — skip
    }
    if (instructions && instructions.trim().length > 0) {
      lines.push(`Guidance for \`${r.id}\`:`);
      lines.push(instructions.trim());
      lines.push("");
    }
  }

  if (shellAccess) {
    lines.push("Discover connectors:");
    lines.push("- `connector_list` tool — returns the live connector set as JSON.");
    lines.push("- `skaile connector list` (CLI) — shell equivalent.");
    lines.push("- `skaile connector <id> --ops` — list a connector's operations.");
    lines.push(
      "Call the `connector_load_skill` tool to load a connector's full instructions into context.",
    );
    lines.push(
      "Interact with connectors via their CLI commands (see loaded skill body for exact syntax).",
    );
  } else {
    lines.push("Discover connectors with the `connector_list` tool (returns live JSON).");
    lines.push(
      "Call the `connector_load_skill` tool to load a connector's full instructions into context.",
    );
    lines.push(
      "Use the `connector_exec` tool to execute connector operations — do not use CLI commands.",
    );
  }

  return lines.join("\n");
}

// ── Private helpers ───────────────────────────────────────────────────────────

function safeHost(source: string): string {
  try {
    return new URL(source).host;
  } catch {
    return source;
  }
}

/** Transport-independent tool descriptor with a Zod raw input shape. */
export interface ConnectorToolDef {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  handler: (input: any) => Promise<unknown>;
}
